import React, { createContext, useContext, useState, useEffect } from 'react';
import { orderService, messageService } from '../services/apiService';
import api from '../services/api';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'delivery', 'oldest'
  const [hideTestOrders, setHideTestOrders] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('hideTestOrders');
    return saved === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [pinnedOrderIds, setPinnedOrderIds] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [customFilters, setCustomFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'unread', or custom filter name

  // Save hideTestOrders preference to localStorage
  useEffect(() => {
    localStorage.setItem('hideTestOrders', hideTestOrders);
  }, [hideTestOrders]);

  // Fetch orders and pinned orders when user is authenticated
  useEffect(() => {
    if (currentUser) {
      fetchOrders();
      fetchPinnedOrders();
      fetchUnreadCounts();
      fetchCustomFilters();
    }
  }, [currentUser]);

  // Fetch messages when order is selected
  useEffect(() => {
    if (selectedOrder) {
      fetchMessages(selectedOrder.id);
    }
  }, [selectedOrder]);

  // Select an order and always refresh its messages (even if already selected)
  const selectOrder = (order) => {
    if (order?.id !== selectedOrder?.id) {
      // Different order — setSelectedOrder triggers the useEffect above
      setSelectedOrder(order);
    } else {
      // Same order — React would bail out on setState, so fetch manually
      if (order?.id) fetchMessages(order.id);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const fetchedOrders = await orderService.getOrders();
      setOrders(fetchedOrders);

      // Update selected order if it exists in the new list
      if (selectedOrder) {
        const updatedSelectedOrder = fetchedOrders.find(o => o.id === selectedOrder.id);
        if (updatedSelectedOrder) {
          setSelectedOrder(updatedSelectedOrder);
        }
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPinnedOrders = async () => {
    try {
      const res = await api.get('/user/pinned-orders');
      setPinnedOrderIds(res.data.pinned_order_ids || []);
    } catch (error) {
      console.error('Failed to fetch pinned orders:', error);
    }
  };

  const pinOrder = async (orderId) => {
    try {
      const res = await api.post(`/user/pinned-orders/${orderId}`);
      setPinnedOrderIds(res.data.pinned_order_ids || []);
      return true;
    } catch (error) {
      console.error('Failed to pin order:', error);
      return false;
    }
  };

  const unpinOrder = async (orderId) => {
    try {
      const res = await api.delete(`/user/pinned-orders/${orderId}`);
      setPinnedOrderIds(res.data.pinned_order_ids || []);
      return true;
    } catch (error) {
      console.error('Failed to unpin order:', error);
      return false;
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const res = await api.get('/user/unread-counts');
      setUnreadCounts(res.data || {});
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    }
  };

  const fetchCustomFilters = async () => {
    try {
      const res = await api.get('/user/filters');
      setCustomFilters(res.data.filters || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const createFilter = async (name) => {
    try {
      const res = await api.post('/user/filters', { name });
      setCustomFilters(res.data.filters || []);
      return true;
    } catch (error) {
      console.error('Failed to create filter:', error);
      return error.response?.data?.detail || 'Failed to create filter';
    }
  };

  const deleteFilter = async (filterName) => {
    try {
      const res = await api.delete(`/user/filters/${encodeURIComponent(filterName)}`);
      setCustomFilters(res.data.filters || []);
      if (activeFilter === filterName) setActiveFilter('all');
      return true;
    } catch (error) {
      console.error('Failed to delete filter:', error);
      return false;
    }
  };

  const addOrderToFilter = async (filterName, orderId) => {
    try {
      const res = await api.post(`/user/filters/${encodeURIComponent(filterName)}/orders/${orderId}`);
      setCustomFilters(res.data.filters || []);
      return true;
    } catch (error) {
      console.error('Failed to add order to filter:', error);
      return false;
    }
  };

  const removeOrderFromFilter = async (filterName, orderId) => {
    try {
      const res = await api.delete(`/user/filters/${encodeURIComponent(filterName)}/orders/${orderId}`);
      setCustomFilters(res.data.filters || []);
      return true;
    } catch (error) {
      console.error('Failed to remove order from filter:', error);
      return false;
    }
  };

  const fetchMessages = async (orderId) => {
    try {
      const fetchedMessages = await messageService.getMessages(orderId);
      setMessages(prev => ({ ...prev, [orderId]: fetchedMessages }));
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const getOrderMessages = (orderId) => {
    return messages[orderId] || [];
  };

  const sendMessage = async (orderId, content, type = 'text') => {
    if (!currentUser) return;

    try {
      const newMessage = await messageService.sendMessage(orderId, content, type);

      // Update local messages
      setMessages(prev => ({
        ...prev,
        [orderId]: [...(prev[orderId] || []), newMessage],
      }));

      // Update order's last message
      setOrders(prev =>
        prev.map(order =>
          order.id === orderId
            ? {
                ...order,
                last_message: type === 'text' ? content : `${type} file`,
                last_message_time: newMessage.timestamp,
              }
            : order
        )
      );

      return newMessage;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  };

  const markAsRead = async (orderId, messageId) => {
    if (!currentUser) return;

    try {
      await messageService.markAsRead(messageId);

      // Update local messages
      setMessages(prev => ({
        ...prev,
        [orderId]: prev[orderId]?.map(msg =>
          msg.id === messageId && !msg.read_by.includes(currentUser.id)
            ? { ...msg, read_by: [...msg.read_by, currentUser.id] }
            : msg
        ),
      }));
      // Update unread count locally
      setUnreadCounts(prev => {
        const current = prev[orderId] || 0;
        if (current <= 1) {
          const { [orderId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [orderId]: current - 1 };
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const setTyping = (orderId, isTyping) => {
    setTypingUsers(prev => ({
      ...prev,
      [orderId]: isTyping,
    }));
  };

  const createOrder = async (orderData) => {
    if (!currentUser) return;

    try {
      const newOrder = await orderService.createOrder(orderData);
      setOrders(prev => [newOrder, ...prev]);
      return newOrder;
    } catch (error) {
      console.error('Failed to create order:', error);
      throw error;
    }
  };

  // Attach unread counts to orders
  const ordersWithUnread = orders.map(order => ({
    ...order,
    unreadCount: unreadCounts[order.id] || 0,
  }));

  const filteredOrders = ordersWithUnread
    .filter(order =>
      order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.auto_generated_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_phone?.includes(searchQuery)
    )
    .filter(order => {
      // Filter out test orders if hideTestOrders is enabled
      if (hideTestOrders) {
        const isTestOrder =
          order.order_number?.toLowerCase().includes('test') ||
          order.title?.toLowerCase().includes('test') ||
          order.customer_name?.toLowerCase().includes('test');
        if (isTestOrder) return false;
      }
      return true;
    })
    .filter(order => {
      // Apply active filter
      if (activeFilter === 'all') return true;
      if (activeFilter === 'unread') return (order.unreadCount || 0) > 0;
      // Custom filter — check if order is in the filter's order_ids
      const customFilter = customFilters.find(f => f.name === activeFilter);
      if (customFilter) return customFilter.order_ids.includes(order.id);
      return true;
    });

  // Sort orders based on selection, with pinned orders always at top
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aPinned = pinnedOrderIds.includes(a.id);
    const bPinned = pinnedOrderIds.includes(b.id);

    // Pinned orders always come first
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;

    if (sortBy === 'recent') {
      const dateA = new Date(a.created_at || a.last_message_time || 0);
      const dateB = new Date(b.created_at || b.last_message_time || 0);
      return dateB - dateA;
    } else if (sortBy === 'oldest') {
      const dateA = new Date(a.created_at || a.last_message_time || 0);
      const dateB = new Date(b.created_at || b.last_message_time || 0);
      return dateA - dateB;
    } else if (sortBy === 'delivery') {
      const dateA = new Date(a.delivery_date || '9999-12-31');
      const dateB = new Date(b.delivery_date || '9999-12-31');
      return dateA - dateB;
    }
    return 0;
  });

  const value = {
    orders: sortedOrders,
    messages,
    selectedOrder,
    setSelectedOrder,
    selectOrder,
    fetchMessages,
    getOrderMessages,
    sendMessage,
    markAsRead,
    typingUsers,
    setTyping,
    createOrder,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    hideTestOrders,
    setHideTestOrders,
    loading,
    refreshOrders: fetchOrders,
    pinnedOrderIds,
    pinOrder,
    unpinOrder,
    unreadCounts,
    refreshUnreadCounts: fetchUnreadCounts,
    customFilters,
    activeFilter,
    setActiveFilter,
    createFilter,
    deleteFilter,
    addOrderToFilter,
    removeOrderFromFilter,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
